import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  TrendingUp, TrendingDown, Banknote, Receipt, AlertTriangle,
  Download, CalendarIcon, FileText, BarChart3, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ManagementReportMenu } from "@/components/Holizontal/ManagementReportMenu";

/* ─── constants ─── */
const branches = ["All Branches", "Nairobi CBD", "Westlands", "Mombasa", "Kisumu", "Nakuru", "Eldoret"];
const productList = ["All Products", "Personal Loan", "Business Loan", "Salary Advance", "Asset Finance"];
const periods = ["Jan 2026", "Feb 2026", "Mar 2026", "Q1 2026", "FY 2026"];

const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", { minimumFractionDigits: 0 }).format(Math.round(n));

/* ─── types ─── */
interface PLLine {
  label: string;
  value: number;
  sub?: PLLine[];
  isTotal?: boolean;
  isSubtotal?: boolean;
  indent?: number;
  positive?: boolean; // true = income, false = expense
}

/* ─── mock monthly series ─── */
const monthlyData = [
  { month: "Jan", interest: 1820000, fees: 410000, penalties: 95000, salaries: 620000, admin: 180000, commissions: 92000, provision: 85000, writeOff: 40000 },
  { month: "Feb", interest: 2100000, fees: 490000, penalties: 120000, salaries: 620000, admin: 210000, commissions: 105000, provision: 100000, writeOff: 55000 },
  { month: "Mar", interest: 1950000, fees: 470000, penalties: 88000, salaries: 620000, admin: 195000, commissions: 97000, provision: 78000, writeOff: 30000 },
  { month: "Apr", interest: 2250000, fees: 530000, penalties: 145000, salaries: 640000, admin: 220000, commissions: 113000, provision: 115000, writeOff: 65000 },
  { month: "May", interest: 2080000, fees: 500000, penalties: 110000, salaries: 640000, admin: 205000, commissions: 104000, provision: 95000, writeOff: 45000 },
  { month: "Jun", interest: 2400000, fees: 560000, penalties: 135000, salaries: 660000, admin: 230000, commissions: 120000, provision: 125000, writeOff: 70000 },
];

const computeSeries = (data: typeof monthlyData) =>
  data.map((m) => {
    const income = m.interest + m.fees + m.penalties;
    const expenses = m.salaries + m.admin + m.commissions + m.provision + m.writeOff;
    return { ...m, income, expenses, netProfit: income - expenses };
  });

const series = computeSeries(monthlyData);

/* ─── build P&L structure ─── */
const buildPL = (filterBranch: string, filterProduct: string, period: string): PLLine[] => {
  // Scale mock data slightly based on filter selection for realism
  const scale = filterBranch === "All Branches" ? 1 : 0.18;
  const pScale = filterProduct === "All Products" ? 1 : 0.28;
  const s = scale * pScale;

  const interest = 14600000 * s;
  const fees_admin = 1980000 * s;
  const fees_insurance = 920000 * s;
  const fees_application = 490000 * s;
  const penalties = 693000 * s;
  const totalIncome = interest + fees_admin + fees_insurance + fees_application + penalties;

  const salaries = 4320000 * s;
  const rent = 780000 * s;
  const utilities = 180000 * s;
  const admin_misc = 420000 * s;
  const commissions = 731000 * s;
  const marketing = 240000 * s;
  const totalOperational = salaries + rent + utilities + admin_misc + commissions + marketing;

  const provision = 730000 * s;
  const writeOff = 305000 * s;
  const totalCredit = provision + writeOff;

  const grossProfit = totalIncome - totalCredit;
  const netProfit = grossProfit - totalOperational;
  const margin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;

  return [
    { label: "INCOME", value: totalIncome, isTotal: true, positive: true },
    { label: "Interest Income", value: interest, indent: 1, positive: true },
    { label: "Admin Fees", value: fees_admin, indent: 1, positive: true },
    { label: "Insurance Fees", value: fees_insurance, indent: 1, positive: true },
    { label: "Application Fees", value: fees_application, indent: 1, positive: true },
    { label: "Penalties Collected", value: penalties, indent: 1, positive: true },
    { label: "", value: 0, isSubtotal: false },

    { label: "COST OF CREDIT RISK", value: totalCredit, isTotal: true, positive: false },
    { label: "Loan Loss Provision", value: provision, indent: 1, positive: false },
    { label: "Write-offs", value: writeOff, indent: 1, positive: false },
    { label: "", value: 0 },

    { label: "GROSS PROFIT", value: grossProfit, isSubtotal: true, positive: grossProfit >= 0 },
    { label: "", value: 0 },

    { label: "OPERATIONAL EXPENSES", value: totalOperational, isTotal: true, positive: false },
    { label: "Staff Salaries & Benefits", value: salaries, indent: 1, positive: false },
    { label: "Rent & Office Space", value: rent, indent: 1, positive: false },
    { label: "Utilities & IT", value: utilities, indent: 1, positive: false },
    { label: "Admin & Miscellaneous", value: admin_misc, indent: 1, positive: false },
    { label: "BDO Commissions", value: commissions, indent: 1, positive: false },
    { label: "Marketing & Advertising", value: marketing, indent: 1, positive: false },
    { label: "", value: 0 },

    { label: "NET PROFIT / (LOSS)", value: netProfit, isSubtotal: true, positive: netProfit >= 0 },
    { label: "Net Margin %", value: margin, isSubtotal: true, positive: margin >= 0 },
  ];
};

/* ─── chart colors ─── */
const COLORS = {
  income: "hsl(142, 70%, 50%)",
  expenses: "hsl(0, 80%, 60%)",
  profit: "hsl(262, 80%, 55%)",
  interest: "hsl(210, 80%, 55%)",
  fees: "hsl(30, 90%, 55%)",
  penalties: "hsl(38, 92%, 50%)",
  salaries: "hsl(0, 80%, 60%)",
  provision: "hsl(15, 70%, 55%)",
  writeOff: "hsl(350, 80%, 55%)",
};

const PIE_COLORS = [
  "hsl(210, 80%, 55%)",
  "hsl(30, 90%, 55%)",
  "hsl(38, 92%, 50%)",
  "hsl(142, 70%, 50%)",
  "hsl(262, 80%, 55%)",
];

export default function ProfitLossPage() {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date(2026, 0, 1));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date(2026, 5, 30));
  const [filterBranch, setFilterBranch] = useState("All Branches");
  const [filterProduct, setFilterProduct] = useState("All Products");
  const [period, setPeriod] = useState("Q1 2026");

  const plLines = useMemo(
    () => buildPL(filterBranch, filterProduct, period),
    [filterBranch, filterProduct, period]
  );

  /* KPI shortcuts */
  const totalIncome = plLines.find((l) => l.label === "INCOME")?.value ?? 0;
  const totalExpenses =
    (plLines.find((l) => l.label === "OPERATIONAL EXPENSES")?.value ?? 0) +
    (plLines.find((l) => l.label === "COST OF CREDIT RISK")?.value ?? 0);
  const netProfit = plLines.find((l) => l.label === "NET PROFIT / (LOSS)")?.value ?? 0;
  const grossProfit = plLines.find((l) => l.label === "GROSS PROFIT")?.value ?? 0;
  const netMargin = plLines.find((l) => l.label === "Net Margin %")?.value ?? 0;

  /* income breakdown for donut */
  const incomeBreakdown = [
    { name: "Interest", value: plLines.find((l) => l.label === "Interest Income")?.value ?? 0 },
    { name: "Admin Fees", value: plLines.find((l) => l.label === "Admin Fees")?.value ?? 0 },
    { name: "Insurance Fees", value: plLines.find((l) => l.label === "Insurance Fees")?.value ?? 0 },
    { name: "Application Fees", value: plLines.find((l) => l.label === "Application Fees")?.value ?? 0 },
    { name: "Penalties", value: plLines.find((l) => l.label === "Penalties Collected")?.value ?? 0 },
  ];

  /* ─── exports ─── */
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Profit & Loss Account", 14, 18);
    doc.setFontSize(10);
    doc.text(`Period: ${period} | Branch: ${filterBranch} | Product: ${filterProduct}`, 14, 26);
    doc.text(`Date Range: ${dateFrom ? format(dateFrom, "dd/MM/yyyy") : "-"} – ${dateTo ? format(dateTo, "dd/MM/yyyy") : "-"}`, 14, 33);

    const body: (string | number)[][] = plLines
      .filter((l) => l.label)
      .map((l) => {
        const indent = "  ".repeat(l.indent ?? 0);
        if (l.label === "Net Margin %") return [indent + l.label, `${l.value.toFixed(1)}%`];
        return [indent + l.label, l.isTotal || l.isSubtotal ? `KES ${fmt(l.value)}` : fmt(l.value)];
      });

    autoTable(doc, {
      startY: 40,
      head: [["Account", "Amount (KES)"]],
      body,
      styles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" } },
      didParseCell: (data) => {
        const row = plLines.filter((l) => l.label)[data.row.index];
        if (row?.isSubtotal) data.cell.styles.fontStyle = "bold";
        if (row?.isTotal) data.cell.styles.textColor = [80, 80, 80];
      },
    });

    doc.save("profit_loss_account.pdf");
  };

  const exportExcel = () => {
    const sheetData = plLines
      .filter((l) => l.label)
      .map((l) => ({
        Account: "  ".repeat(l.indent ?? 0) + l.label,
        Amount: l.label === "Net Margin %" ? `${l.value.toFixed(1)}%` : Math.round(l.value),
      }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    ws["!cols"] = [{ wch: 36 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "P&L");
    XLSX.writeFile(wb, "profit_loss_account.xlsx");
  };

  const DatePick = ({ label, value, onChange }: { label: string; value?: Date; onChange: (d?: Date) => void }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("justify-start text-left font-normal min-w-[130px]", !value && "text-muted-foreground")}>
          <CalendarIcon className="h-4 w-4 mr-1" />
          {value ? format(value, "dd/MM/yyyy") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus className="p-3 pointer-events-auto" />
      </PopoverContent>
    </Popover>
  );

  /* ─── P&L Statement Row ─── */
  const PLRow = ({ line }: { line: PLLine }) => {
    if (!line.label) return <tr><td colSpan={2} className="py-1" /></tr>;
    const isPercent = line.label === "Net Margin %";
    const displayValue = isPercent ? `${line.value.toFixed(1)}%` : `KES ${fmt(line.value)}`;

    if (line.isSubtotal) {
      return (
        <tr className="border-t-2 border-border">
          <td className="py-2 px-3 font-bold text-sm">{line.label}</td>
          <td className={cn(
            "py-2 px-3 text-right font-bold text-sm tabular-nums",
            line.positive ? "text-[hsl(142,70%,40%)]" : "text-destructive"
          )}>
            {displayValue}
          </td>
        </tr>
      );
    }

    if (line.isTotal) {
      return (
        <tr className="bg-muted/40">
          <td className="py-2 px-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
            {line.label}
          </td>
          <td className={cn(
            "py-2 px-3 text-right font-semibold text-sm tabular-nums",
            line.positive ? "text-[hsl(142,70%,40%)]" : "text-[hsl(0,80%,50%)]"
          )}>
            {displayValue}
          </td>
        </tr>
      );
    }

    return (
      <tr className="hover:bg-muted/20 transition-colors">
        <td className="py-1.5 px-3 text-sm text-muted-foreground" style={{ paddingLeft: `${12 + (line.indent ?? 0) * 20}px` }}>
          {line.label}
        </td>
        <td className="py-1.5 px-3 text-right text-sm tabular-nums text-foreground">
          {fmt(line.value)}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" /> Profit &amp; Loss Account
        </h1>
        <Badge variant="outline" className="text-xs px-3 py-1">{period}</Badge>
      </motion.div>

       <ManagementReportMenu />

      {/* ── Filter Bar ── */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <DatePick label="From" value={dateFrom} onChange={setDateFrom} />
            <DatePick label="To" value={dateTo} onChange={setDateTo} />
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{productList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={exportPDF}>
                <Download className="h-4 w-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={exportExcel}>
                <Download className="h-4 w-4 mr-1" /> Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          {
            label: "Total Income",
            value: `KES ${fmt(totalIncome)}`,
            icon: TrendingUp,
            gradient: "gradient-card-green",
            trend: "+8.4%",
            up: true,
          },
          {
            label: "Total Expenses",
            value: `KES ${fmt(totalExpenses)}`,
            icon: TrendingDown,
            gradient: "gradient-card-red",
            trend: "+3.1%",
            up: false,
          },
          {
            label: "Gross Profit",
            value: `KES ${fmt(grossProfit)}`,
            icon: Banknote,
            gradient: "gradient-card-blue",
            trend: "+12.2%",
            up: true,
          },
          {
            label: "Net Profit",
            value: `KES ${fmt(netProfit)}`,
            icon: Receipt,
            gradient: netProfit >= 0 ? "gradient-card-green" : "gradient-card-red",
            trend: netProfit >= 0 ? "+5.7%" : "-2.3%",
            up: netProfit >= 0,
          },
          {
            label: "Net Margin",
            value: `${netMargin.toFixed(1)}%`,
            icon: BarChart3,
            gradient: netMargin >= 15 ? "gradient-card-green" : netMargin >= 5 ? "gradient-card-orange" : "gradient-card-red",
            trend: `${netMargin >= 0 ? "+" : ""}${(netMargin - 12).toFixed(1)}pp`,
            up: netMargin >= 12,
          },
        ].map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card className={`${c.gradient} text-white shadow-card`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <c.icon className="h-5 w-5 opacity-90" />
                  <span className={cn("text-xs flex items-center gap-0.5 font-medium", c.up ? "text-white/90" : "text-white/70")}>
                    {c.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {c.trend}
                  </span>
                </div>
                <p className="text-xl font-bold leading-tight">{c.value}</p>
                <p className="text-xs opacity-80 mt-0.5">{c.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* ── Main content tabs ── */}
      <Tabs defaultValue="statement" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="statement">P&amp;L Statement</TabsTrigger>
          <TabsTrigger value="trends">Monthly Trends</TabsTrigger>
          <TabsTrigger value="breakdown">Income Breakdown</TabsTrigger>
          <TabsTrigger value="expenses">Expense Breakdown</TabsTrigger>
        </TabsList>

        {/* ── P&L Statement ── */}
        <TabsContent value="statement">
          <Card className="shadow-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base">Profit &amp; Loss Statement</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {filterBranch} · {filterProduct} · {period}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plLines.map((line, i) => <PLRow key={i} line={line} />)}
                  </tbody>
                </table>
              </div>

              {/* Summary banner */}
              <div className={cn(
                "mx-4 mb-4 mt-2 rounded-xl p-4 flex flex-wrap gap-6 items-center justify-between border",
                netProfit >= 0
                  ? "bg-[hsl(142,70%,50%)]/10 border-[hsl(142,70%,50%)]/30"
                  : "bg-destructive/10 border-destructive/30"
              )}>
                <div>
                  <p className="text-xs text-muted-foreground">Net Result for Period</p>
                  <p className={cn("text-2xl font-bold", netProfit >= 0 ? "text-[hsl(142,70%,40%)]" : "text-destructive")}>
                    KES {fmt(netProfit)}
                  </p>
                </div>
                <div className="flex gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">Net Margin</p>
                    <p className="text-lg font-bold">{netMargin.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Gross Profit</p>
                    <p className="text-lg font-bold">KES {fmt(grossProfit)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Income</p>
                    <p className="text-lg font-bold">KES {fmt(totalIncome)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Monthly Trends ── */}
        <TabsContent value="trends">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Income vs Expenses vs Net Profit</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={series} barSize={16}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" name="Income" fill={COLORS.income} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill={COLORS.expenses} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="netProfit" name="Net Profit" fill={COLORS.profit} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Net Profit Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={series}>
                    <defs>
                      <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.profit} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.profit} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} contentStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="netProfit" name="Net Profit" stroke={COLORS.profit} fill="url(#profitGrad)" strokeWidth={2} />
                    <Line type="monotone" dataKey="income" name="Income" stroke={COLORS.income} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="expenses" name="Expenses" stroke={COLORS.expenses} strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Monthly P&L table */}
            <Card className="shadow-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Monthly Summary Table</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {["Month", "Interest", "Fees+Penalties", "Total Income", "Op. Expenses", "Provision+W/O", "Net Profit", "Margin%"].map((h) => (
                        <th key={h} className="py-2 px-3 text-right first:text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {series.map((row, i) => {
                      const opEx = row.salaries + row.admin + row.commissions;
                      const creditEx = row.provision + row.writeOff;
                      const feesPen = row.fees + row.penalties;
                      const margin = row.income > 0 ? ((row.netProfit / row.income) * 100).toFixed(1) : "0.0";
                      return (
                        <tr key={row.month} className={cn("border-b border-border hover:bg-muted/20 transition-colors", i % 2 === 0 ? "" : "bg-muted/10")}>
                          <td className="py-2 px-3 font-medium">{row.month}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(row.interest)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(feesPen)}</td>
                          <td className="py-2 px-3 text-right tabular-nums font-semibold text-[hsl(142,70%,40%)]">{fmt(row.income)}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">{fmt(opEx)}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-destructive">{fmt(creditEx)}</td>
                          <td className={cn("py-2 px-3 text-right tabular-nums font-bold", row.netProfit >= 0 ? "text-[hsl(142,70%,40%)]" : "text-destructive")}>
                            {fmt(row.netProfit)}
                          </td>
                          <td className={cn("py-2 px-3 text-right tabular-nums font-medium", parseFloat(margin) >= 15 ? "text-[hsl(142,70%,40%)]" : parseFloat(margin) >= 5 ? "text-[hsl(30,90%,45%)]" : "text-destructive")}>
                            {margin}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/40">
                      <td className="py-2 px-3 font-bold text-sm">TOTAL</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums">{fmt(series.reduce((a, r) => a + r.interest, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums">{fmt(series.reduce((a, r) => a + r.fees + r.penalties, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums text-[hsl(142,70%,40%)]">{fmt(series.reduce((a, r) => a + r.income, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums">{fmt(series.reduce((a, r) => a + r.salaries + r.admin + r.commissions, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums text-destructive">{fmt(series.reduce((a, r) => a + r.provision + r.writeOff, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums text-[hsl(142,70%,40%)]">{fmt(series.reduce((a, r) => a + r.netProfit, 0))}</td>
                      <td className="py-2 px-3 text-right font-bold tabular-nums">
                        {(() => { const ti = series.reduce((a, r) => a + r.income, 0); const tn = series.reduce((a, r) => a + r.netProfit, 0); return ti > 0 ? ((tn / ti) * 100).toFixed(1) + "%" : "0%"; })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Income Breakdown ── */}
        <TabsContent value="breakdown">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Income Sources – Donut</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={incomeBreakdown} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {incomeBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Income Mix by Month (Stacked)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={series} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="interest" name="Interest" stackId="a" fill={COLORS.interest} />
                    <Bar dataKey="fees" name="Fees" stackId="a" fill={COLORS.fees} />
                    <Bar dataKey="penalties" name="Penalties" stackId="a" fill={COLORS.penalties} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Income detail table */}
            <Card className="shadow-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Income Detail</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {["Source", "Amount (KES)", "% of Total Income"].map((h) => (
                        <th key={h} className="py-2 px-4 text-right first:text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incomeBreakdown.map((row, i) => (
                      <tr key={row.name} className={cn("border-b border-border hover:bg-muted/20 transition-colors", i % 2 === 0 ? "" : "bg-muted/10")}>
                        <td className="py-2 px-4 font-medium flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i] }} />
                          {row.name}
                        </td>
                        <td className="py-2 px-4 text-right tabular-nums">{fmt(row.value)}</td>
                        <td className="py-2 px-4 text-right tabular-nums">
                          {totalIncome > 0 ? ((row.value / totalIncome) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/40">
                      <td className="py-2 px-4 font-bold">Total Income</td>
                      <td className="py-2 px-4 text-right font-bold text-[hsl(142,70%,40%)] tabular-nums">{fmt(totalIncome)}</td>
                      <td className="py-2 px-4 text-right font-bold">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Expense Breakdown ── */}
        <TabsContent value="expenses">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Expense Categories – Stacked Bar</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={series} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="salaries" name="Salaries" stackId="e" fill={COLORS.salaries} />
                    <Bar dataKey="admin" name="Admin" stackId="e" fill="hsl(210, 70%, 55%)" />
                    <Bar dataKey="commissions" name="Commissions" stackId="e" fill={COLORS.fees} />
                    <Bar dataKey="provision" name="Provision" stackId="e" fill={COLORS.provision} />
                    <Bar dataKey="writeOff" name="Write-offs" stackId="e" fill={COLORS.writeOff} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Expense Mix – Donut</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Salaries", value: plLines.find((l) => l.label === "Staff Salaries & Benefits")?.value ?? 0 },
                        { name: "Rent & Office", value: plLines.find((l) => l.label === "Rent & Office Space")?.value ?? 0 },
                        { name: "Admin", value: plLines.find((l) => l.label === "Admin & Miscellaneous")?.value ?? 0 },
                        { name: "Commissions", value: plLines.find((l) => l.label === "BDO Commissions")?.value ?? 0 },
                        { name: "Provision", value: plLines.find((l) => l.label === "Loan Loss Provision")?.value ?? 0 },
                        { name: "Write-offs", value: plLines.find((l) => l.label === "Write-offs")?.value ?? 0 },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={105}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {[0,1,2,3,4,5].map((i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => `KES ${fmt(v)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Expense detail table */}
            <Card className="shadow-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Expense Detail</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {["Category", "Amount (KES)", "% of Total Income"].map((h) => (
                        <th key={h} className="py-2 px-4 text-right first:text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: "Staff Salaries & Benefits" },
                      { label: "Rent & Office Space" },
                      { label: "Utilities & IT" },
                      { label: "Admin & Miscellaneous" },
                      { label: "BDO Commissions" },
                      { label: "Marketing & Advertising" },
                      { label: "Loan Loss Provision" },
                      { label: "Write-offs" },
                    ].map(({ label }, i) => {
                      const val = plLines.find((l) => l.label === label)?.value ?? 0;
                      return (
                        <tr key={label} className={cn("border-b border-border hover:bg-muted/20 transition-colors", i % 2 === 0 ? "" : "bg-muted/10")}>
                          <td className="py-2 px-4 font-medium flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                            {label}
                          </td>
                          <td className="py-2 px-4 text-right tabular-nums text-destructive">{fmt(val)}</td>
                          <td className="py-2 px-4 text-right tabular-nums text-muted-foreground">
                            {totalIncome > 0 ? ((val / totalIncome) * 100).toFixed(1) : 0}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/40">
                      <td className="py-2 px-4 font-bold">Total Expenses</td>
                      <td className="py-2 px-4 text-right font-bold text-destructive tabular-nums">{fmt(totalExpenses)}</td>
                      <td className="py-2 px-4 text-right font-bold text-muted-foreground">
                        {totalIncome > 0 ? ((totalExpenses / totalIncome) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
